import { postRequest, getCSRFToken } from '@/helpers/request';

export default {
  namespaced: true,
  state: () => ({
    isGrainFoxAssistantDialogOpen: false,
    showPaywall: false,
    isChatUsageLoading: false,
    isDataLoading: false,
    isSinoaInitialized: false,
    messages: [],
    chatUsage: null,
    chatHistory: {},
    faqItems: [],
    showFaqs: true,
    subscriptionCart: {
      selectedBillingCycle: null,
      selectedPlanIndex: 0,
      selectedPlan: null,
      selectedAddons: [],
      selectedSinoa: null,
      autoRenew: false,
    },
    followUpQuestions: [],
    partnerWorkflows: [],
    threadId: null,
    memoryId: null,
    currentStreamInterval: null,
    deepDive: false,
    feedbackByInteraction: {},
  }),

  getters: {
    hasRealMessages: (state) => state.messages.length > 0,
    isLimitReached: (state) => state.chatUsage?.is_limit_reached || false,
    hasChatHistory: (state) => {
      const pinned = state.chatHistory?.pinnedQuestions || [];
      const recent = state.chatHistory?.recentQuestions || [];
      return pinned.length > 0 || recent.length > 0;
    },
    isSinoaLoading: (state) => state.isChatUsageLoading || state.isDataLoading,
    isStreaming: (state) => state.messages.some((m) => m.is_pending === true && m.question_type !== 'faq' && m.question_type !== 'qna'),
    getFeedbackForInteraction: (state) => (interactionId) => state.feedbackByInteraction[interactionId] || null,
    hasPartnerWorkflows: (state) => state.partnerWorkflows.length > 0,
  },

  mutations: {
    setGrainFoxAssistantDialogOpen(state, isOpen) {
      state.isGrainFoxAssistantDialogOpen = isOpen;
    },

    setShowPaywall(state, value) {
      state.showPaywall = value;
    },

    setIsChatUsageLoading(state, value) {
      state.isChatUsageLoading = value;
    },

    setIsDataLoading(state, value) {
      state.isDataLoading = value;
    },

    addUserMessage(state, {
      question, date, id = null, is_pinned = false, question_type = null,
    }) {
      state.messages.push({
        id,
        question,
        answer: null,
        created_at: date || new Date().toISOString(),
        is_pinned,
        is_pending: true,
        is_complete: false,
        question_type,
      });
    },

    updateMessage(state, { index, updates }) {
      const msg = state.messages[index];
      if (msg) {
        Object.assign(msg, updates);
      }
    },

    clearMessages(state) {
      state.messages = [];
    },

    setChatUsage(state, usage) {
      state.chatUsage = usage;
      if (usage?.is_limit_reached === true) {
        state.followUpQuestions = [];
      }
    },

    updateMessagePinStatus(state, { messageId, is_pinned }) {
      const message = state.messages.find((m) => m.id === messageId);
      if (message) {
        message.is_pinned = is_pinned;
      }
    },

    setChatHistory(state, interactions) {
      const processedInteractions = interactions.map((interaction) => ({
        ...interaction,
        displayQuestion: interaction.summary || interaction.question,
      }));

      const pinnedQuestions = processedInteractions.filter((i) => i.is_pinned);
      const recentQuestions = processedInteractions.filter((i) => !i.is_pinned);
      state.chatHistory = { pinnedQuestions, recentQuestions };
    },

    setSubscriptionCart(state, values) {
      state.subscriptionCart = { ...state.subscriptionCart, ...values };
    },

    setShowFaqs(state, value) {
      state.showFaqs = value;
    },

    setFaqItems(state, items) {
      state.faqItems = items;
    },

    setFollowUpQuestions(state, questions) {
      state.followUpQuestions = questions;
    },

    setPartnerWorkflows(state, workflows) {
      state.partnerWorkflows = workflows;
    },

    setConversationIds(state, { threadId, memoryId }) {
      state.threadId = threadId;
      state.memoryId = memoryId;
    },

    setConversationIdsFromMessage(state, { threadId, memoryId }) {
      if (threadId) state.threadId = threadId;
      if (memoryId) state.memoryId = memoryId;
    },

    setStreamInterval(state, interval) {
      if (state.currentStreamInterval) {
        clearInterval(state.currentStreamInterval);
      }
      state.currentStreamInterval = interval;
    },

    clearStreamInterval(state) {
      if (state.currentStreamInterval) {
        clearInterval(state.currentStreamInterval);
        state.currentStreamInterval = null;
      }
    },

    setDeepDive(state, value) {
      state.deepDive = value;
    },

    setFeedbackValue(state, { interactionId, value, reason = null }) {
      const feedbackData = state.feedbackByInteraction[interactionId] || { value: 0, thumbs_up: 0, thumbs_down: 0, reason: null };
      if (reason !== undefined) {
        feedbackData.reason = reason;
      }
      feedbackData.value = value;
      state.feedbackByInteraction = { ...state.feedbackByInteraction, [interactionId]: feedbackData };
    },

    setFeedbackCounts(state, { interactionId, thumbs_up, thumbs_down }) {
      const feedbackData = state.feedbackByInteraction[interactionId] || { value: 0, thumbs_up: 0, thumbs_down: 0 };
      feedbackData.thumbs_up = thumbs_up;
      feedbackData.thumbs_down = thumbs_down;
      state.feedbackByInteraction = { ...state.feedbackByInteraction, [interactionId]: feedbackData };
    },

    setSinoaInitialized(state, value) {
      state.isSinoaInitialized = value;
    },

    addValidationErrorMessage(state, errorText) {
      state.messages.push({
        id: null,
        question: null,
        answer: errorText,
        created_at: new Date().toISOString(),
        is_pinned: false,
        is_pending: false,
        is_complete: true,
        question_type: 'validation_error',
        source: 'system',
      });
    },
  },

  actions: {
    async initializeSinoa({ commit, dispatch, state }) {
      if (state.isSinoaInitialized) {
        return;
      }

      commit('setIsChatUsageLoading', true);
      await dispatch('fetchChatUsage');
      commit('setIsChatUsageLoading', false);

      commit('setIsDataLoading', true);
      await dispatch('fetchFaqItems');
      await dispatch('getChatHistory');
      commit('setIsDataLoading', false);

      commit('setSinoaInitialized', true);
    },

    async sendMessage({
      commit, dispatch, state, getters, rootState, rootGetters,
    }, { question }) {
      commit('clearStreamInterval');
      commit('addUserMessage', { question });
      commit('setFollowUpQuestions', []);
      commit('setPartnerWorkflows', []);
      commit('setShowPaywall', false);
      const index = state.messages.length - 1;

      try {
        if (getters.isLimitReached) {
          commit('updateMessage', {
            index,
            updates: {
              answer: 'Limit reached.',
              is_pending: false,
              is_complete: true,
            },
          });
          commit('setShowPaywall', true);
          return;
        }

        const requestPayload = {
          question,
          user_id: rootState.user.id,
          is_basic_user: rootGetters['chargebee/isBasic'],
          deep_dive: state.deepDive,
        };
        if (state.threadId) requestPayload.thread_id = state.threadId;
        if (state.memoryId) requestPayload.memory_id = state.memoryId;
        const csrfToken = await getCSRFToken();
        const response = await fetch('/chatbot/process-message/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'same-origin',
          body: JSON.stringify(requestPayload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalResponse = null;

        const streamState = {
          userMessages: [],
          followUpQuestions: [],
          threadId: null,
          memoryId: null,
          powerBiUrl: null,
          partnerWorkflows: [],
          partialJson: '',
        };

        const tryParseJson = (jsonString) => {
          const toParse = streamState.partialJson + jsonString;

          try {
            const parsed = JSON.parse(toParse);
            streamState.partialJson = '';
            return parsed;
          } catch (e) {
            if (toParse.trim().startsWith('{') || toParse.trim().startsWith('[')) {
              streamState.partialJson = toParse;
            }
            return null;
          }
        };

        const processStreamEvent = (parsedData) => {
          if (parsedData.kind === 'error' && parsedData.payload?.message) {
            return {
              answer: parsedData.payload.message,
              follow_up_questions: [],
              has_svg: false,
            };
          }

          if (parsedData.id || parsedData.answer || parsedData.is_clarification) {
            const result = { ...parsedData };
            if (!result.follow_up_questions?.length && streamState.followUpQuestions.length > 0) {
              result.follow_up_questions = streamState.followUpQuestions;
            }
            if (!result.power_bi_url && streamState.powerBiUrl) {
              result.power_bi_url = streamState.powerBiUrl;
            }
            if (!result.partner_workflows?.length && streamState.partnerWorkflows.length > 0) {
              result.partner_workflows = streamState.partnerWorkflows;
            }

            return result;
          }

          const { channel, kind, payload } = parsedData;

          if (channel === 'internal') {
            if (kind === 'ack' && payload) {
              streamState.threadId = payload.thread_id || streamState.threadId;
              streamState.memoryId = payload.memory_id || streamState.memoryId;
            }
          } else if (channel === 'user') {
            if ((kind === 'text' || kind === 'clarification') && payload?.message) {
              streamState.userMessages.push(payload.message.trim());
            } else if (kind === 'plot' && payload) {
              streamState.powerBiUrl = payload.power_bi_url || streamState.powerBiUrl;
              if (payload.title) {
                streamState.userMessages.push(`# ${payload.title}`);
              }
            } else if (kind === 'questions' && payload?.questions) {
              streamState.followUpQuestions = payload.questions;
            } else if (kind === 'partner_workflows' && payload?.partner_workflows) {
              streamState.partnerWorkflows = payload.partner_workflows;
            } else if (kind === 'error' && payload?.message) {
              return {
                answer: payload.message,
                follow_up_questions: [],
                has_svg: false,
              };
            }
          }

          return null;
        };

        const processLine = (line) => {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine === 'data:') {
            return null;
          }

          let jsonString;
          if (trimmedLine.startsWith('data: ')) {
            jsonString = trimmedLine.slice(6);
          } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
            jsonString = trimmedLine;
          } else if (streamState.partialJson) {
            jsonString = trimmedLine;
          } else {
            return null;
          }

          const parsedData = tryParseJson(jsonString);
          if (parsedData) {
            return processStreamEvent(parsedData);
          }

          return null;
        };

        const buildResponseFromStreamState = () => {
          if (streamState.userMessages.length === 0) {
            return null;
          }
          const answer = streamState.userMessages.join('\n\n').trim();
          return {
            answer,
            follow_up_questions: streamState.followUpQuestions,
            has_svg: answer.toLowerCase().includes('<svg'),
            thread_id: streamState.threadId,
            memory_id: streamState.memoryId,
            power_bi_url: streamState.powerBiUrl,
            partner_workflows: streamState.partnerWorkflows,
          };
        };

        const processStream = async () => {
          const readChunk = async () => {
            const { done, value } = await reader.read();

            if (done) {
              if (buffer.trim()) {
                const result = processLine(buffer);
                if (result) {
                  finalResponse = result;
                }
              }
              if (!finalResponse) {
                finalResponse = buildResponseFromStreamState();
              }
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            lines.forEach((line) => {
              const result = processLine(line);
              if (result) {
                finalResponse = result;
              }
            });

            await readChunk();
          };

          await readChunk();
        };

        await processStream();

        if (finalResponse) {
          commit('updateMessage', {
            index,
            updates: {
              id: finalResponse.id,
              is_pinned: finalResponse.is_pinned || false,
              source: 'live',
              power_bi_url: finalResponse.power_bi_url || null,
            },
          });

          if (finalResponse.thread_id || finalResponse.memory_id) {
            commit('setConversationIds', {
              threadId: finalResponse.thread_id,
              memoryId: finalResponse.memory_id,
            });
          }

          if (finalResponse.partner_workflows?.length > 0) {
            commit('setPartnerWorkflows', finalResponse.partner_workflows);
          }

          const fullText = finalResponse.answer || 'Our agents are currently experiencing information retrieval difficulties. Please try again later. If the problem persists, please contact our support team.';
          const followUpQuestions = finalResponse.follow_up_questions || [];
          const hasSvg = finalResponse.has_svg || fullText.toLowerCase().includes('<svg');

          if (hasSvg) {
            commit('updateMessage', {
              index,
              updates: {
                answer: fullText,
                is_pending: false,
                is_complete: true,
              },
            });
            if (followUpQuestions.length > 0 && !getters.isLimitReached && !state.showPaywall) {
              commit('setFollowUpQuestions', followUpQuestions);
            }
          } else {
            dispatch('streamMessage', {
              index,
              fullText,
              followUpQuestions,
            });
          }
        } else {
          throw new Error('No valid response received from stream');
        }
      } catch (error) {
        commit('updateMessage', {
          index,
          updates: {
            answer: 'Our agents are currently experiencing information retrieval difficulties. Please try again later. If the problem persists, please contact our support team.',
            is_pending: false,
            is_complete: true,
          },
        });
      } finally {
        dispatch('fetchChatUsage');
        dispatch('getChatHistory');
      }
    },

    streamMessage({ commit, getters, state }, { index, fullText, followUpQuestions = [] }) {
      commit('clearStreamInterval');

      commit('updateMessage', {
        index,
        updates: { answer: '', is_pending: true, is_complete: false },
      });

      let currentIndex = 0;
      const streamingSpeed = 1;

      const streamInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          const currentText = fullText.substring(0, currentIndex + 1);
          commit('updateMessage', {
            index,
            updates: { answer: currentText },
          });
          currentIndex += 1;
        } else {
          commit('updateMessage', {
            index,
            updates: { answer: fullText, is_pending: false, is_complete: true },
          });

          if (followUpQuestions.length > 0 && !getters.isLimitReached && !state.showPaywall) {
            commit('setFollowUpQuestions', followUpQuestions);
          }

          clearInterval(streamInterval);
          commit('clearStreamInterval');
        }
      }, streamingSpeed);

      commit('setStreamInterval', streamInterval);
    },

    async fetchChatUsage({ commit }) {
      try {
        const response = await postRequest('/chatbot/chat-usage/');
        commit('setChatUsage', response);
      } catch (error) {
        this._vm.$snackbar?.error('Failed to fetch chat usage data.');
      }
    },

    async applyBundleCredit({ dispatch }, payload) {
      try {
        await postRequest('/chatbot/apply-bundle-credit/', {
          internal_name: payload.internalName,
          auto_renew: payload.autoRenew || false,
          quantity: payload.quantity || 1,
        });
        this._vm.$snackbar?.success('Bundle credit applied successfully.');
        dispatch('fetchChatUsage');
      } catch (error) {
        this._vm.$snackbar?.error('Failed to apply bundle credit.');
      }
    },

    async togglePinMessage({ state, commit, dispatch }, messageId) {
      const message = state.messages.find((msg) => msg.id === messageId);
      if (!message) return;

      const newPinnedValue = !message.is_pinned;

      try {
        await postRequest('/chatbot/update_chat_interaction/', {
          chat_id: messageId,
          is_pinned: newPinnedValue,
        });
        commit('updateMessagePinStatus', { messageId, is_pinned: newPinnedValue });
        dispatch('getChatHistory');
      } catch (error) {
        this._vm.$snackbar?.error('Failed to update pin status');
      }
    },

    async getChatHistory({ commit }) {
      try {
        const response = await postRequest('/chatbot/chat-history/');
        const { interactions = [] } = response ?? {};
        commit('setChatHistory', interactions);
      } catch (error) {
        this._vm.$snackbar?.error('Failed to fetch chat history.');
      }
    },

    async fetchFaqItems({ commit }) {
      try {
        const response = await postRequest('/chatbot/fetch_latest_faqs/');
        commit('setFaqItems', response.faqs);
      } catch (error) {
        this._vm.$snackbar?.error('Failed to load FAQ questions');
      }
    },

    showStoredAnswer({ commit, state }, item) {
      commit('clearStreamInterval');
      commit('clearMessages');
      commit('setFollowUpQuestions', []);
      commit('setShowPaywall', false);

      if (item.thread_id || item.memory_id) {
        commit('setConversationIdsFromMessage', {
          threadId: item.thread_id,
          memoryId: item.memory_id,
        });
      }

      const questionType = item.question_type || null;

      commit('addUserMessage', {
        question: item.displayQuestion || item.question,
        date: item.created_at,
        id: item.id,
        is_pinned: item.is_pinned,
        question_type: questionType,
      });

      const index = state.messages.length - 1;

      commit('updateMessage', {
        index,
        updates: {
          answer: item.answer,
          is_pending: false,
          is_complete: true,
          is_overlay_gated: questionType === 'qna',
          displayQuestion: item.displayQuestion || item.question,
          power_bi_url: item.power_bi_url || null,
        },
      });
    },

    async showStoredAnswerWithStream({ commit, state, dispatch }, item) {
      commit('clearStreamInterval');

      commit('clearMessages');
      commit('setFollowUpQuestions', []);
      commit('setShowPaywall', false);

      if (item.thread_id || item.memory_id) {
        commit('setConversationIdsFromMessage', {
          threadId: item.thread_id,
          memoryId: item.memory_id,
        });
      }

      const questionType = item.question_type || null;

      commit('addUserMessage', {
        question: item.displayQuestion || item.question,
        date: new Date().toISOString(),
        id: null,
        is_pinned: false,
        question_type: questionType,
      });

      const index = state.messages.length - 1;

      if (questionType === 'faq') {
        try {
          const response = await postRequest('/chatbot/faq_interaction/', {
            question: item.question,
            answer: item.answer,
            faq_id: item.id,
            thread_id: item.thread_id,
            memory_id: item.memory_id,
          });

          commit('updateMessage', {
            index,
            updates: {
              id: response.id,
              created_at: response.created_at,
              is_pinned: response.is_pinned || false,
            },
          });

          if (response.thread_id || response.memory_id) {
            commit('setConversationIds', {
              threadId: response.thread_id,
              memoryId: response.memory_id,
            });
          }

          if (response.id && response.user_feedback !== undefined && response.user_feedback !== null) {
            commit('setFeedbackValue', {
              interactionId: response.id,
              value: response.user_feedback,
            });
          }
        } catch (error) {
          this._vm.$snackbar?.error('Failed to record FAQ interaction');
        }
      }

      dispatch('streamMessage', {
        index,
        fullText: item.answer,
        followUpQuestions: item.follow_up_questions || [],
      });

      commit('updateMessage', {
        index,
        updates: {
          displayQuestion: item.displayQuestion || item.question,
        },
      });
    },

    async fetchDeepDiveSettings({ commit }) {
      try {
        const response = await postRequest('/chatbot/settings/');
        commit('setDeepDive', response.deep_dive || false);
      } catch {
        // ignore
      }
    },

    async updateDeepDiveSettings({ commit }, deepDive) {
      try {
        await postRequest('/chatbot/settings/update/', { deep_dive: deepDive });
        commit('setDeepDive', deepDive);
      } catch (error) {
        this._vm.$snackbar?.error('Failed to update Deep Dive setting');
      }
    },

    async recordQnaInteraction({ dispatch, commit, state }, item) {
      if (!item?.question || !item?.answer) {
        return;
      }

      try {
        const payload = {
          question: item.question,
          answer: item.answer,
        };

        if (item.thread_id) {
          payload.thread_id = item.thread_id;
        }
        if (item.memory_id) {
          payload.memory_id = item.memory_id;
        }

        const response = await postRequest('/chatbot/qna_interaction/', payload);

        if (response?.id && state.messages.length > 0) {
          const lastIndex = state.messages.length - 1;
          commit('updateMessage', {
            index: lastIndex,
            updates: { id: response.id },
          });
        }

        if (response.thread_id || response.memory_id) {
          commit('setConversationIds', {
            threadId: response.thread_id,
            memoryId: response.memory_id,
          });
        }

        dispatch('fetchChatUsage');
        dispatch('getChatHistory');

        // eslint-disable-next-line consistent-return
        return response;
      } catch (error) {
        this._vm.$snackbar?.error('Failed to record interaction');
        throw error;
      }
    },

    async trackCtaClick(_, ctaId) {
      if (!ctaId) return;
      try {
        await postRequest('/chatbot/cta/click/', { cta_id: ctaId });
      } catch {
        // ignore
      }
    },

    async handleContractScanResponse({ commit, dispatch, state }, response) {
      if (!response?.success) {
        return;
      }

      commit('clearStreamInterval');
      commit('clearMessages');
      commit('setFollowUpQuestions', []);
      commit('setShowPaywall', false);

      const question = response.question || 'Contract scan';
      const answer = response.answer || 'Contract processed successfully.';

      commit('addUserMessage', {
        question,
        date: new Date().toISOString(),
        id: response.interaction_id || null,
        is_pinned: false,
        question_type: 'live',
      });

      const index = state.messages.length - 1;

      commit('updateMessage', {
        index,
        updates: {
          id: response.interaction_id,
          answer,
          is_pending: false,
          is_complete: true,
          source: 'live',
        },
      });

      if (response.thread_id || response.memory_id) {
        commit('setConversationIds', {
          threadId: response.thread_id,
          memoryId: response.memory_id,
        });
      }

      dispatch('fetchChatUsage');
      dispatch('getChatHistory');
    },

    async upsertFeedback({ commit, state }, { interactionId, value, reason = null }) {
      try {
        if (!interactionId) {
          throw new Error('interaction_id is required');
        }

        const currentFeedback = state.feedbackByInteraction[interactionId] || { value: 0 };
        const currentValue = currentFeedback.value || 0;
        const newValue = currentValue === value ? 0 : value;

        const payload = {
          interaction_id: interactionId,
          value: newValue,
        };

        if (reason) {
          payload.reason = reason;
        }

        const response = await postRequest('/chatbot/feedback/upsert/', payload);
        commit('setFeedbackValue', { interactionId, value: newValue, reason: response.reason });
        commit('setFeedbackCounts', {
          interactionId,
          thumbs_up: response.thumbs_up || 0,
          thumbs_down: response.thumbs_down || 0,
        });

        return response;
      } catch (error) {
        this._vm.$snackbar?.error('Failed to submit feedback');
        throw error;
      }
    },

    async addSinoaValidationErrors({ commit }, errorText) {
      commit('addValidationErrorMessage', errorText);
    },
  },
};
